import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; 
import { internalApi, REACT_APP_SSO_URI } from '@/utils/request';
import { persistor } from '@/App/store';
import { 
  setCurrentUser,  
  setCurrentAccount, 
  setAuthStatus, 
  setInitialCheckComplete 
} from '@/features/Auth/authSlice';
import { selectCurrentAccount } from '@/features/Auth/selectors';
import { checkOnboardingStatus, resetOnboardingState } from '@/features/OnBoardingFlow';
import { getTrackNameForAccount, getStepsForTrack } from '@/Account/onboardingTracks';  
import { setAccountData } from '@/features/Account/accountSlice';
import { setActiveOrganization } from '@/features/Navigation/contextSlice';
import styles from './AuthGate.module.scss'; 

const MARKETING_URL = process.env.REACT_APP_MARKETING_APP || 'https://noviopus.com';

const LoadingScreen = () => {
  const { t } = useTranslation('common');
  return (
    <div className={styles.loadingScreen}>
      <div className={styles.title}>{t('common.app-name', 'Noviopus Business')}</div>
      <div className={styles.message}>{t('auth.verifying-session', 'Verifying session...')}</div>
    </div>
  );
};

const AuthGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { ready } = useTranslation(['common', 'navigation', 'layout', 'dashboard', 'rec_dashboard']); // Check if critical NS are loaded
  
  const [checkComplete, setCheckComplete] = useState(false);
  const [fetchedData, setFetchedData] = useState<{ user: any; account: any } | null>(null);

  const reduxAccount = useSelector(selectCurrentAccount);

  // ... (checkAuthenticationAndSetup Effect UNCHANGED) ...
  useEffect(() => {
    const checkAuthenticationAndSetup = async () => {
      try {
        // 1. One-Time Remote Epoch Sync (clears stale redux-persist & ghost cookies once when AUTH_EPOCH changes)
        try {
          const ssoVersionRes = await fetch(`${REACT_APP_SSO_URI}/users/version`, {
            credentials: 'include',
          });
          if (ssoVersionRes.ok) {
            const { authEpoch } = await ssoVersionRes.json();
            const localEpoch = localStorage.getItem('noviopus_auth_epoch');
            if (authEpoch && localEpoch !== authEpoch) {
              try {
                await persistor.purge();
              } catch (_) {}
              localStorage.removeItem('persist:root');
              localStorage.removeItem('currentOrganizationId');
              sessionStorage.clear();
              dispatch(resetOnboardingState());
              await fetch(`${REACT_APP_SSO_URI}/users/auth/sanitize`, {
                method: 'POST',
                credentials: 'include',
              });
              localStorage.setItem('noviopus_auth_epoch', authEpoch);
            }
          }
        } catch (epochErr) {
          console.warn('[AuthGate Epoch Sync] Skipped:', epochErr);
        }

        const userRes = await internalApi.get('/business/current-user');
        const userData = userRes.data || userRes;
        
        let foundUser = null;
        let foundAccount = null;

        if (userData && (userData.status === 200 || userData.id)) {
           foundUser = userData;
           
           dispatch(setCurrentUser(userData));
           dispatch(setAuthStatus(true));

           try {
             const filter = { include: ['companies'] };
             const accRes = await internalApi.get(`/users/${userData.id}/accounts?filter=${JSON.stringify(filter)}`);
             
             const accData = accRes.data || accRes;
             if (accData && Array.isArray(accData) && accData.length > 0) {
                foundAccount = accData[0];
                dispatch(setCurrentAccount(foundAccount));
                dispatch(setAccountData(foundAccount));  

                const savedOrgId = localStorage.getItem('currentOrganizationId');
                
                if (foundAccount.companies && Array.isArray(foundAccount.companies)) {
                    let targetOrg;
                    if (savedOrgId) {
                        targetOrg = foundAccount.companies.find((c: any) => (c.id === savedOrgId || c._id === savedOrgId));
                    }
                    if (!targetOrg && foundAccount.companies.length > 0) {
                        targetOrg = foundAccount.companies[0];
                    }
                    if (targetOrg) {
                        // FIX: Use setActiveOrganization with just the ID
                        dispatch(setActiveOrganization(targetOrg.id || targetOrg._id));
                    }
                } // <-- FIX: This closing bracket was missing!
             } else {
                dispatch(setCurrentAccount(null));
             }
           } catch (accError: any) {
             console.warn('[AuthGate] Account lookup failed:', accError.message);
             dispatch(setCurrentAccount(null));
           }

           if (!foundAccount) {
               try {
                   const initRes = await internalApi.post('/business/initialize-user');
                   const initializedUser = initRes.data || initRes;
                   
                   if (initializedUser && initializedUser.id) {
                       foundUser = initializedUser;
                       dispatch(setCurrentUser(foundUser));
                       
                       const retryRes = await internalApi.get(`/users/${initializedUser.id}/accounts`);
                       const retryData = retryRes.data || retryRes;
                       if (Array.isArray(retryData) && retryData.length > 0) {
                           foundAccount = retryData[0];
                           dispatch(setCurrentAccount(foundAccount));
                       }
                   }
               } catch (initErr) {
                   console.error('[AuthGate] Initialization failed', initErr);
               }
           }

           dispatch(checkOnboardingStatus());

        } else {
           dispatch(setAuthStatus(false));
           dispatch(setCurrentUser(null));
        }
        setFetchedData({ user: foundUser, account: foundAccount });
      } catch (error) {
        console.error("[AuthGate] Auth check failed", error);
        dispatch(setAuthStatus(false));
        dispatch(setCurrentUser(null));
        setFetchedData({ user: null, account: null });
      } finally {
        setCheckComplete(true);
        dispatch(setInitialCheckComplete(true));
      }
    };
    checkAuthenticationAndSetup();
  }, [dispatch]);

  // ... (Routing Logic Effect UNCHANGED) ...
  useEffect(() => {
      if (!checkComplete || !fetchedData) return;

      const { user } = fetchedData;

      if (!user) {
          window.location.href = MARKETING_URL;
          return;
      }

      const activeAccount = reduxAccount || fetchedData.account;

      const isComplete = activeAccount && activeAccount.onboardingComplete === true;
      const stage = activeAccount?.onboardingStage || 0;

      const isOnboardingPath = 
          location.pathname.startsWith('/account/setup') || 
          location.pathname.startsWith('/organization/create');
      
      const isEditPath = location.pathname.includes('/edit') || location.pathname.includes('/account/setup/edit');

      if (!isComplete && activeAccount) {
         if (!isOnboardingPath) {
             const trackName = getTrackNameForAccount(activeAccount);
             const steps = getStepsForTrack(trackName);
             const target = steps[stage] || steps[0];
             navigate(target, { replace: true });
         }
      }
      else if (isComplete) {
         if (isOnboardingPath && !isEditPath) {
             navigate('/dashboard/recruitment', { replace: true });
         } else if (location.pathname === '/') {
             navigate('/dashboard/recruitment', { replace: true });
         }
      }
  }, [checkComplete, fetchedData, navigate, location.pathname, reduxAccount]);

  // FIX: Wait for Translations
  if (!checkComplete || !fetchedData || !ready) {
    return <LoadingScreen />;
  }

  const { user } = fetchedData;
  if (!user) return <LoadingScreen />; 

  const activeAccount = reduxAccount || fetchedData.account;
  const isComplete = activeAccount && activeAccount.onboardingComplete === true;
  const isOnboardingPath = 
      location.pathname.startsWith('/account/setup') || 
      location.pathname.startsWith('/organization/create');
  
  const isEditPath = location.pathname.includes('/edit') || location.pathname.includes('/account/setup/edit');

  if (!isComplete && !isOnboardingPath && !isEditPath) {
      return <LoadingScreen />;
  }

  return <>{children}</>;
};

export default AuthGate;